// Chat screen: the video stage (remote + local self-view + watermark), the
// status overlay, the controls bar (stop, skip, go back, report), and the chat
// panel (messages, typing indicator, composer with file sharing). Returns a root
// node and an imperative `api` the app drives. ASCII only.

import { t } from '../i18n.js';
import { linkifyNodes } from './linkify.js';
import { el, ORB_SVG, toast } from '../dom.js';
import { createTextSizeControl } from './text-size.js';
import { createRoomVideo } from './room-video.js';
import { addSystemNotice } from './system-notice.js';
import { createEmojiPicker } from './emoji.js';
import { makeImageViewable } from './lightbox.js';
import {
  renderQuote,
  attachMessageActions,
  applyReaction,
  hasMyReaction,
  removeMessage,
  applyLinkPreview,
} from './message-ui.js';
import { ReportReason, languageName, PROMPT_IDS, PROMPT_WHO } from '@lumi/shared';
import { createMediaControls } from './media-controls.js';
import { createRemoteAudioSink } from '../remote-audio.js';
import { WAVE_COPY, WAVE_OFFER_MS, waveResultText } from './regulars.js';

export function renderChat(cb) {
  const remoteVideo = el('video', { id: 'remoteVideo', autoplay: '', playsinline: '' });
  // Speaker volume that works on desktop AND iOS (where media-element volume is read-only).
  const remoteAudio = createRemoteAudioSink();
  const localVideo = el('video', { id: 'localVideo', autoplay: '', playsinline: '', muted: '' });
  // One quiet label in the corner. It was briefly tiled across the picture --
  // harder to crop out, and far too much to sit and talk in front of. What it
  // SAYS is the part that matters (see setWatermark): the viewer's own name.
  const watermark = el('div', { class: 'watermark', 'aria-hidden': 'true' });

  const statusTitle = el('h2', { text: t('Finding someone new...') });
  const statusSub = el('p', {});
  // Your own "come and talk to me" link. Lives in the waiting overlay,
  // because that is the only moment it is any use: once you are chatting,
  // inviting somebody else into a 1:1 makes no sense.
  const inviteRow = el('div', { class: 'invite-row hidden' });
  const statusOverlay = el('div', { class: 'status-overlay' }, [
    el('div', { class: 'orb', html: ORB_SVG }),
    statusTitle,
    statusSub,
    inviteRow,
  ]);

  const skipBtn = el('button', {
    class: 'btn btn-icon btn-primary',
    title: t('Skip to the next person'),
    'aria-label': 'Skip',
    html: nextIcon(),
    disabled: '',
    onClick: () => cb.onSkip?.(),
  });
  const skipWrap = el('div', { class: 'skip-wrap' }, [skipBtn]);

  const stopBtn = el('button', {
    class: 'btn btn-icon btn-ghost',
    // Reported as "when I click leave, it takes me to the homepage, not to
    // holding for the next person". It always did, and it should: Skip is the
    // way to the next person. The button was simply lying about itself -- it
    // was labelled "Leave", which reads as leaving the CHAT, while its own
    // tooltip said "Stop chatting" and its behaviour was to leave the SITE.
    // Naming the destination is the whole fix.
    title: t('Leave the chat and go back to the homepage'),
    'aria-label': 'Go home',
    html: closeIcon(),
    onClick: () => cb.onStop?.(),
  });

  const reportBtn = el('button', {
    class: 'btn btn-icon btn-warn',
    title: t('Report this person'),
    'aria-label': 'Report',
    html: flagIcon(),
    onClick: () => openReport(cb),
  });

  // Moderator-only: remove this person from Lumi for 15 minutes (a reason is required).
  const kickBtn = el('button', {
    class: `btn btn-icon btn-danger${cb.canModerate ? '' : ' hidden'}`,
    title: t('Remove this person for 15 minutes (moderator)'),
    'aria-label': 'Remove',
    html: kickIcon(),
    onClick: () => openKick(cb),
  });

  // Self mic/camera controls + the settings popover (mic/speaker volume, noise suppression,
  // background blur, filter). Shared with the group room (ui/media-controls.js) so the two
  // screens behave identically. The speaker target is per-screen: here it is the one remote.
  const media = createMediaControls({
    canVideo: cb.canVideo,
    getLocalStream: () => localVideo.srcObject,
    onEnableMic: () => cb.onEnableMic?.(),
    onEnableCam: () => cb.onEnableCam?.(),
    onMicVolume: (g) => cb.onMicVolume?.(g),
    onSpeakerVolume: (v) => remoteAudio.setVolume(v),
    onEffect: (kind, value) => cb.onEffect?.(kind, value),
    onCamToggle: (on) => {
      localVideo.classList.toggle('cam-off', !on);
      cb.onCamToggle?.(on); // so the text-only layout can hand the space back
    },
  });
  const { micBtn, camBtn, settingsBtn, settingsPanel } = media;

  // In a text chat these icons sit over nothing, and a row of bare circles is a
  // guessing game -- "how do I leave?" most of all. Each carries a label that
  // the text-only layout reveals. Set as a data attribute and drawn with
  // generated content, because the mic and camera buttons rewrite their own
  // innerHTML on every toggle and would wipe out a child element.
  micBtn.dataset.label = 'Mic';
  camBtn.dataset.label = 'Camera';
  settingsBtn.dataset.label = 'Settings';
  stopBtn.dataset.label = 'Home';
  skipBtn.dataset.label = 'Skip';
  reportBtn.dataset.label = 'Report';
  kickBtn.dataset.label = 'Remove';

  // Camera-off start: the one control the reveal needs. A pair's cameras open
  // only when BOTH have tapped it, so the label is the whole instruction. Words
  // rather than an icon, because "show my camera" is a promise about what the
  // other person is about to see, and a promise wants saying.
  const revealBtn = el('button', {
    class: 'btn btn-primary reveal-btn hidden',
    type: 'button',
    text: t('Show my camera'),
    title: t('Open your camera to them. It opens once they have done the same.'),
    onClick: () => cb.onReveal?.(),
  });

  // Explicit chat, between two adults who both chose that lane. Hidden unless
  // the server says this pair may have it -- a minor's client is never told it
  // exists, and nothing anywhere else on the site mentions it. Both must
  // agree; either can withdraw with this same button, at any moment.
  const explicitBtn = el('button', {
    class: 'btn explicit-btn hidden',
    type: 'button',
    'aria-pressed': 'false',
    text: t('Explicit: off'),
    onClick: () => cb.onExplicit?.(),
  });

  const controls = el('div', { class: 'controls' }, [
    micBtn,
    camBtn,
    revealBtn,
    explicitBtn,
    settingsBtn,
    stopBtn,
    skipWrap,
    reportBtn,
    kickBtn,
  ]);

  // Your own badges, on your own picture. Everyone else could see these on you;
  // you could not, so there was no way to confirm a staff badge was live.
  const selfTags = el('div', { class: 'self-tags' });
  const selfView = el('div', { class: 'self-view' }, [localVideo, selfTags]);
  // The soft placeholder over the remote picture while the cameras are still
  // closed. It sits UNDER the status overlay and the controls: while you are
  // connecting the overlay says so, and once you are, this says why there is
  // no face yet and what opens one. The self-view stays: you can see you.
  const revealTitle = el('h3', { text: t('Cameras open when you both tap Show my camera') });
  const revealSub = el('p', { text: '' });
  const revealPlaceholder = el('div', { class: 'reveal-placeholder hidden', role: 'status' }, [
    el('div', { class: 'orb', html: ORB_SVG }),
    revealTitle,
    revealSub,
  ]);
  const stage = el('div', { class: 'stage' }, [
    remoteVideo,
    watermark,
    revealPlaceholder,
    selfView,
    statusOverlay,
    settingsPanel,
    controls,
  ]);

  // ---- chat panel ----
  // Who you are talking to, kept where it is ALWAYS visible. The name used to
  // appear only in the waiting overlay, which disappears the moment you connect
  // -- so from the first message on, nothing on screen said who this was.
  const panelTitle = el('span', { class: 'panel-title', text: t('Chat') });
  // In a 1:1 there is exactly one other person, and the panel already names
  // them -- so that is who a reaction is from.
  const peerName = () => {
    const shown = (panelTitle.textContent || '').trim();
    return shown && shown !== 'Chat' ? shown : 'Them';
  };
  const messages = el('div', { class: 'messages', id: 'messages' });
  // Pinned system notices sit above the transcript (see ui/system-notice.js).
  const sysPins = el('div', { class: 'sys-pins' });
  const typing = el('div', { class: 'typing' });

  const input = el('input', {
    type: 'text',
    placeholder: t('Say something nice...'),
    maxlength: '4000',
    autocomplete: 'off',
    'aria-label': 'Message',
  });
  let typingTimer = null;
  input.addEventListener('input', () => {
    cb.onTyping?.(true);
    clearTimeout(typingTimer);
    typingTimer = setTimeout(() => cb.onTyping?.(false), 1500);
  });
  const submit = () => {
    const text = input.value.trim();
    if (!text) return;
    cb.onSend?.(text, replyTo);
    input.value = '';
    cancelReply();
    emojiPicker.close();
    cb.onTyping?.(false);
  };
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submit();
    }
  });

  const fileInput = el('input', {
    type: 'file',
    class: 'visually-hidden',
    accept: 'image/*', // images only -- the server refuses anything else
    title: t('Send an image'),
    onChange: (e) => {
      const file = e.target.files && e.target.files[0];
      if (file) cb.onFile?.(file);
      e.target.value = '';
    },
  });
  const attachBtn = el('button', {
    class: 'attach',
    type: 'button',
    title: t('Share a photo, video, or audio file'),
    'aria-label': 'Share a file',
    text: '+',
    onClick: () => fileInput.click(),
  });
  const sendBtn = el('button', {
    class: 'send',
    type: 'button',
    'aria-label': 'Send',
    html: sendIcon(),
    onClick: submit,
  });

  const emojiPicker = createEmojiPicker(input);
  const composer = el('div', { class: 'composer' }, [attachBtn, fileInput, emojiPicker.button, input, sendBtn]);

  // ---- Reply, react, unsend --------------------------------------------------
  let replyTo = null;
  const replyBar = el('div', { class: 'reply-bar hidden' });
  function startReply(quote) {
    replyTo = quote;
    replyBar.replaceChildren(
      el('span', { class: 'reply-label', text: `Replying to ${quote.who}: ` }),
      el('span', { class: 'reply-text', text: quote.text.slice(0, 140) }),
      el('button', { class: 'reply-cancel', title: t('Cancel reply'), text: 'x', onClick: cancelReply }),
    );
    replyBar.classList.remove('hidden');
    input.focus();
  }
  function cancelReply() {
    replyTo = null;
    replyBar.classList.add('hidden');
    replyBar.replaceChildren();
  }
  function toggleReaction(id, emoji) {
    const on = !hasMyReaction(messages, id, emoji);
    applyReaction(messages, id, emoji, 'me', on, peerName); // show it at once
    cb.onReact?.(id, emoji, on);
  }
  function deleteOwn(id) {
     
    if (!window.confirm('Delete this message for both of you?')) return;
    removeMessage(messages, id);
    cb.onUnsend?.(id);
  }
  function decorate(node, { id, who, text }) {
    if (!id) return;
    attachMessageActions(node, {
      id,
      mine: who === 'me',
      text,
      who: who === 'me' ? 'You' : 'Them',
      onReply: startReply,
      onReact: toggleReaction,
      onDelete: who === 'me' ? deleteOwn : null,
    });
  }

  // Something to watch or listen to together. A pair has no host: there are
  // two of you and nobody is the audience, so either may pin and either may
  // press play. The server agrees -- it reports sharedControl for a pair
  // whatever anybody asks for.
  const roomVideo = createRoomVideo({
    onControl: (action, atMs) => cb.onVideoControl?.(action, atMs),
    onPin: (url) => cb.onVideoPin?.(url),
    onShare: () => {}, // nothing to share: it always is
  });

  // "prefers Spanish": the partner's language hint, next to their name. A
  // hint, not a rule -- it says what they would rather speak, so you can meet
  // them there if you can. Nothing about how you were matched.
  const peerLangTag = el('span', { class: 'peer-lang hidden' });
  // The ten-minute clock, when this chat has one. Counts down, and turns amber
  // for the last thirty seconds, when the transcript says so as well.
  const timedPill = el('span', { class: 'timed-pill hidden', role: 'timer', 'aria-label': 'Time left in this chat' });

  // Where the regulars bell goes when the feature exists for this band. Empty
  // until app.js puts one in, so the header is unchanged for everyone else.
  const bellSlot = el('span', { class: 'bell-slot' });
  const panel = el('div', { class: 'panel' }, [
    el('div', { class: 'panel-head' }, [
      el('span', { class: 'panel-head-title' }, [el('span', { html: ORB_SVG }), panelTitle, peerLangTag, timedPill]),
      el('span', { class: 'panel-head-tools' }, [
        bellSlot,
        // The readable-size control lives WITH the messages rather than behind a
        // settings menu: someone who cannot read the chat should not have to go
        // hunting through menus to fix that.
        createTextSizeControl(),
      ]),
    ]),
    roomVideo.root,
    sysPins,
    messages,
    typing,
    replyBar,
    emojiPicker.panel,
    composer,
  ]);

  const root = el('div', { class: 'chat' }, [stage, panel]);

  // Tap the video to go immersive -- hide the controls AND the chat panel so the full
  // video shows (great on a phone where the chat eats half the screen). Double-tap to
  // bring everything back. Taps on the controls/buttons are ignored.
  const immersiveHint = el('div', { class: 'immersive-hint', text: t('Double-tap to show the chat and controls') });
  stage.append(immersiveHint);

  // Live captions: the partner's transcribed speech, shown briefly over the video.
  const captionBar = el('div', { class: 'caption-bar' });
  stage.append(captionBar);
  let captionTimer = null;
  function showCaption(handle, text) {
    captionBar.textContent = handle ? `${handle}: ${text}` : text;
    captionBar.classList.add('show');
    clearTimeout(captionTimer);
    captionTimer = setTimeout(() => captionBar.classList.remove('show'), 5000);
  }

  // Verified-moderator badge and the Plus mark share one row, so a partner who
  // is both does not get two things stacked on the same corner.
  const partnerModBadge = el('div', { class: 'partner-mod-badge', text: t('Moderator') });
  const partnerPlusMark = el('div', { class: 'partner-plus-mark', title: t('Lumi Plus member'), text: 'PLUS' });
  const partnerTags = el('div', { class: 'partner-tags' }, [partnerModBadge, partnerPlusMark]);
  // Verified LUMI STAFF. Granted by an administrator and carried in the session
  // token, so nobody can type their way into it -- which is exactly why it has
  // to be loud: it is the one signal that says this really is the Lumi team.
  const partnerStaffBadge = el('div', { class: 'partner-staff-badge' }, [
    el('span', { class: 'staff-check', text: 'OK' }),
    el('span', { text: t('Verified Lumi staff') }),
  ]);
  // One wrapper for both. Over a video it is display: contents -- it does not
  // exist as a box, and each badge floats over the picture where it always
  // did. In a TEXT chat there is no picture, and floating put "Moderator" and
  // "Verified Lumi staff" squarely on top of the Mic and Camera buttons. There
  // the wrapper becomes a row of its own, above the controls.
  stage.append(el('div', { class: 'partner-badges' }, [partnerTags, partnerStaffBadge]));
  // Held as state and applied together: the Plus mark depends on whether the
  // staff badge is showing, so toggling each independently would render
  // whichever call arrived last.
  let partnerIsStaff = false;
  let partnerIsPlus = false;
  function applyPartnerTags() {
    partnerStaffBadge.classList.toggle('show', partnerIsStaff);
    // Staff carry Plus perks, but the staff badge STANDS ALONE. Showing PLUS
    // next to it adds nothing a viewer needs and dilutes the one signal that
    // says this really is the Lumi team.
    partnerPlusMark.classList.toggle('show', partnerIsPlus && !partnerIsStaff);
  }
  // Same rule as a room tile: staff stands alone, moderator is a separate claim.
  function setSelfBadges({ staff = false, moderator = false, plus = false } = {}) {
    selfTags.replaceChildren();
    if (staff) selfTags.append(el('span', { class: 'staff-badge', title: t('Verified Lumi staff'), text: t('LUMI STAFF') }));
    if (moderator) selfTags.append(el('span', { class: 'mod-badge', title: t('Verified moderator'), text: t('MOD') }));
    if (plus && !staff) selfTags.append(el('span', { class: 'plus-mark', title: t('Lumi Plus member'), text: 'PLUS' }));
  }
  function setPartnerModerator(isMod) {
    partnerModBadge.classList.toggle('show', !!isMod);
  }
  function setPartnerStaff(isStaff) {
    partnerIsStaff = !!isStaff;
    applyPartnerTags();
  }
  // The subtle Plus mark. Quiet on purpose -- it marks a supporting member, not
  // an authority, and it says nothing about how anyone was matched.
  function setPartnerPlus(isPlus) {
    partnerIsPlus = !!isPlus;
    applyPartnerTags();
  }
  // DOUBLE-tap the video to toggle immersive mode (hide/show the controls + chat). A
  // single tap does nothing, so tapping a control never accidentally flattens the UI.
  let tapTimer = null;
  stage.addEventListener('click', (e) => {
    if (e.target.closest('.controls, .settings-panel, .skip-wrap, .panel, button, input, a, select, label')) return;
    if (tapTimer) {
      clearTimeout(tapTimer);
      tapTimer = null;
      root.classList.toggle('immersive'); // second tap within the window -> toggle
    } else {
      tapTimer = setTimeout(() => {
        tapTimer = null; // a lone tap -> ignore
      }, 280);
    }
  });

  // ---- imperative api ----
  const api = {
    /** The shared video state, as the server broadcasts it. */
    setVideo: (s) => roomVideo.setState(s),
    /** Adults only -- and in a pair, being allowed means being in charge. */
    setVideoAllowed: (on) => roomVideo.setOwner(!!on),
    remoteVideo,
    localVideo,
    showCaption,
    setPartnerModerator,
    setPartnerStaff,
    setPartnerPlus,
    setSelfBadges,
    // Sync the effect controls to saved preferences (without firing onEffect).
    initEffects(prefs) {
      media.api.initEffects(prefs);
    },
    setLocalStream(stream) {
      localVideo.srcObject = stream;
      localVideo.classList.remove('cam-off');
      media.api.setLocalStream(stream); // tracks, mic/cam buttons, popover, ranges, volume
    },
    // Show the PROCESSED (filtered/blurred) video in the self-view so effects are visible,
    // while keeping the original audio track so mic-mute still works.
    setSelfVideo(track) {
      const cur = localVideo.srcObject;
      const audio = cur ? cur.getAudioTracks() : [];
      localVideo.srcObject = new MediaStream([...(track ? [track] : []), ...audio]);
    },
    setRemoteStream(stream) {
      remoteVideo.srcObject = stream;
      if (stream) remoteAudio.attach('remote', stream, remoteVideo);
      else remoteAudio.detach('remote');
    },
    /**
     * The overlay over the video stage. `fadeAfterMs` makes it a NOTICE rather
     * than a state: it takes itself away again. Use it for anything that is
     * true once and then stops being true -- a greeting, a confirmation --
     * and leave it off for a state somebody is genuinely still waiting out,
     * like "Finding someone new...".
     *
     * Any later setStatus (or hideStatus) cancels a pending fade, so a notice
     * can never reach across and dismiss the status that replaced it.
     */
    setStatus(title, sub, fadeAfterMs) {
      clearTimeout(api._statusTimer);
      api._statusTimer = null;
      statusTitle.textContent = title;
      statusSub.textContent = sub || '';
      statusOverlay.classList.remove('hidden');
      if (fadeAfterMs > 0) {
        api._statusTimer = setTimeout(() => {
          api._statusTimer = null;
          statusOverlay.classList.add('hidden');
        }, fadeAfterMs);
      }
    },
    hideStatus() {
      clearTimeout(api._statusTimer);
      api._statusTimer = null;
      statusOverlay.classList.add('hidden');
    },
    pulse() {
      stage.classList.remove('connect-pulse');
      void stage.offsetWidth;
      stage.classList.add('connect-pulse');
    },
    setWatermark(text) {
      watermark.textContent = text || '';
    },
    /**
     * Where this pair stands on explicit content: null (or not available) hides
     * the control entirely, so a pair that may not have it never sees it named.
     * @param {{ available?: boolean, me?: boolean, them?: boolean, both?: boolean }|null} state
     */
    setExplicit(state) {
      const available = !!state && !!state.available;
      explicitBtn.classList.toggle('hidden', !available);
      if (!available) return;
      explicitBtn.classList.toggle('agreed', !!state.both);
      explicitBtn.setAttribute('aria-pressed', state.me ? 'true' : 'false');
      if (state.both) explicitBtn.textContent = 'Explicit is on. Tap to stop';
      else if (state.me) explicitBtn.textContent = 'Waiting for them to agree';
      else if (state.them) explicitBtn.textContent = 'They agreed. Tap to agree';
      else explicitBtn.textContent = 'Explicit: off';
      explicitBtn.title = state.both
        ? 'Either of you can stop this at any moment. Reporting works exactly as it always does.'
        : 'Both of you have to agree before anything changes, and either can stop it at any moment.';
    },
    /**
     * The ONE place on the site where the explicit lane is chosen, in the
     * settings popover. Only built when the server has told this session it may
     * have it -- adults, switch on -- so it is never advertised to anyone else.
     * @param {{ available: boolean, on: boolean }|null} state
     */
    setExplicitLane(state) {
      if (!state || !state.available) {
        api._explicitLaneRow?.remove();
        api._explicitLaneRow = null;
        return;
      }
      if (!api._explicitLaneRow) {
        const check = el('input', {
          type: 'checkbox',
          onChange: (e) => cb.onExplicitLane?.(e.target.checked === true),
        });
        api._explicitLaneCheck = check;
        api._explicitLaneRow = el('label', { class: 'fx-row' }, [
          check,
          el('span', {
            text: t('Explicit chat (18+)'),
            title: t('Match me only with adults who chose this too. It changes who you meet next, not this chat, and you both still have to agree once you are in one.'),
          }),
        ]);
        settingsPanel.append(el('div', { class: 'fx-divider' }), api._explicitLaneRow);
      }
      api._explicitLaneCheck.checked = !!state.on;
    },
    setSkipEnabled(enabled) {
      skipBtn.disabled = !enabled;
    },
    addText(who, text, opts = {}) {
      // Links are clickable: a Lumi Link always, a link off the site only
      // when the app says this is an adult chat. Elements, never markup.
      const node = addMsg(messages, who, el('span', { class: 'msg-text' }, linkifyNodes(text, { external: !!cb.externalLinks })), opts.replyTo);
      decorate(node, { id: opts.id, who, text });
    },
    addSystem(text, opts = {}) {
      // The only path that renders the system style -- see ui/system-notice.js
      // for why that matters.
      return addSystemNotice(messages, sysPins, text, opts);
    },
    /**
     * A message from Lumi staff during a text drop-in.
     *
     * A message, not a system notice: a person is typing to you and expects an
     * answer, and dressing that as an automated notice would misrepresent both
     * what it is and what it wants. The badge is built HERE rather than taken
     * from a handle, so it stays as unforgeable as the notice styling is.
     */
    addStaffMessage(text) {
      const node = el('div', { class: 'msg them staff-msg' }, [
        el('span', { class: 'who' }, [
          el('span', { class: 'who-name', text: t('Lumi Staff') }),
          el('span', { class: 'staff-badge', title: t('Verified Lumi staff'), text: t('LUMI STAFF') }),
        ]),
        // Lumi's own voice: every link in it is clickable.
        el('span', { class: 'msg-text' }, linkifyNodes(text, { external: true })),
      ]);
      messages.append(node);
      messages.scrollTop = messages.scrollHeight;
      return node;
    },
    addFile(who, { mime, url, name }, opts = {}) {
      let media;
      if (mime.startsWith('image/')) media = el('img', { src: url, alt: name });
      else if (mime.startsWith('video/')) media = el('video', { src: url, controls: '' });
      else media = el('audio', { src: url, controls: '' });
      // Shared files are not screened by the moderators before they arrive, so
      // the person who received one gets to take it off their screen at once,
      // and to report the file itself rather than just the sender.
      const wrap = el('div', { class: 'file-msg' }, [media]);
      if (mime.startsWith('image/')) makeImageViewable(media, messages);
      if (who !== 'me') fileControls({ media, mime, wrap, onReport: cb.onReport });
      const node = addMsg(messages, who, wrap);
      // And it is a message, so it gets a message's actions: reply, react, and
      // -- for your own -- unsend. A shared image had none of the three.
      if (opts.id && who !== 'system') {
        attachMessageActions(node, {
          id: opts.id,
          mine: who === 'me',
          text: name || 'image',
          who: who === 'me' ? 'You' : 'them',
          onReply: startReply,
          onReact: toggleReaction,
          onDelete: who === 'me' ? deleteOwn : null,
        });
      }
    },
    linkPreview(id, card) {
      applyLinkPreview(messages, id, card);
    },
    peerReaction(id, emojiChar, who, on) {
      applyReaction(messages, id, emojiChar, who || 'them', on, peerName);
    },
    peerUnsend(id) {
      removeMessage(messages, id);
    },
    /**
     * Text-only layout: give the chat the whole screen while there is no video
     * to show, and hand the space back the moment any camera comes on. Called
     * with the CURRENT state, so it is safe to call repeatedly.
     */
    setVideoActive(active) {
      root.classList.toggle('text-only', !active);
    },
    /** Name the person you are chatting with, in the panel header. */
    setPeerName(name) {
      panelTitle.textContent = name ? `Chat with ${name}` : 'Chat';
    },
    setTyping(isTyping) {
      typing.textContent = isTyping ? 'typing...' : '';
    },
    offerGoBack(durationMs, onClick) {
      api.clearGoBack();
      const ring = el('span', { class: 'goback-ring' });
      const btn = el('button', {
        class: 'btn btn-icon',
        title: t('Go back to that person'),
        'aria-label': 'Go back',
        html: backIcon(),
        onClick: () => {
          api.clearGoBack();
          onClick?.();
        },
      });
      btn.style.background = 'var(--lumi-spark)';
      const wrap = el('div', { class: 'skip-wrap goback-wrap' }, [btn, ring]);
      skipWrap.after(wrap);
      api._gobackWrap = wrap;
      api._gobackTimer = setTimeout(() => api.clearGoBack(), durationMs);
    },
    clearGoBack() {
      clearTimeout(api._gobackTimer);
      api._gobackWrap?.remove();
      api._gobackWrap = null;
    },
    clearMessages() {
      while (messages.firstChild) messages.removeChild(messages.firstChild);
      typing.textContent = '';
    },
    focusInput() {
      input.focus();
    },

    // ---- Lower-pressure starts ------------------------------------------------

    /**
     * Camera-off start. `state` is { me, them, both, hasVideo }, or null to
     * clear (the chat ended). The placeholder and the button show only while
     * there is something to wait for: a camera of yours to offer, or theirs
     * already offered. Once both have, everything here gets out of the way.
     */
    setReveal(state) {
      const pending = !!state && !state.both && (!!state.hasVideo || !!state.them);
      stage.classList.toggle('reveal-pending', pending);
      revealPlaceholder.classList.toggle('hidden', !pending);
      revealBtn.classList.toggle('hidden', !pending || !state.hasVideo || !!state.me);
      if (!pending) return;
      if (state.them) {
        revealTitle.textContent = 'They are ready';
        revealSub.textContent = state.hasVideo
          ? 'Cameras open when you tap Show my camera.'
          : 'Turn your camera on to show yours too.';
      } else if (state.me) {
        revealTitle.textContent = 'You are ready';
        revealSub.textContent = 'Cameras open when they tap Show my camera too.';
      } else {
        revealTitle.textContent = 'Cameras open when you both tap Show my camera';
        revealSub.textContent = 'Say hi first if you like. Only you can see you until then.';
      }
    },
    /**
     * The Lumi Link: your own personal link, on the waiting screen.
     *
     * Shown only while waiting and only once there is a code. Copy rather
     * than a share sheet as the primary action: the point is to paste it
     * somewhere else entirely, and a copied string works in every app.
     */
    setInviteLink(url) {
      inviteRow.replaceChildren();
      inviteRow.classList.toggle('hidden', !url);
      if (!url) return;
      const field = el('input', { class: 'invite-url', type: 'text', readOnly: true, value: url, 'aria-label': 'Your Lumi Link' });
      inviteRow.append(
        el('p', { class: 'invite-lead', text: t('Quiet here? Bring someone. Post your Lumi Link and whoever opens it talks to you, not to the queue.') }),
        el('div', { class: 'invite-controls' }, [
          field,
          el('button', {
            class: 'btn btn-primary btn-sm',
            text: t('Copy my Lumi Link'),
            onClick: () => {
              field.select?.();
              navigator.clipboard?.writeText(url).then(
                () => toast('Lumi Link copied. Paste it anywhere.'),
                () => toast(url),
              );
            },
          }),
          navigator.share
            ? el('button', {
                class: 'btn btn-ghost btn-sm',
                text: t('Share'),
                onClick: () => navigator.share({ text: 'Come and talk to me on Lumi', url }).catch(() => {}),
              })
            : null,
        ].filter(Boolean)),
        el('p', { class: 'invite-note muted', text: t('Your Lumi Link keeps working while you are here, and for a day after. It only ever connects you to someone in your own age group.') }),
      );
    },
    /** "prefers Spanish": the partner's language hint, or null to clear. */
    setPeerLang(tag) {
      const name = tag ? languageName(tag) : '';
      peerLangTag.textContent = name ? `prefers ${name}` : '';
      peerLangTag.classList.toggle('hidden', !name);
    },
    /** The ten-minute clock: { endsAt, warnAt } to start it, null to clear. */
    setTimed(state) {
      clearInterval(api._timedTimer);
      api._timedTimer = null;
      timedPill.classList.remove('soon');
      if (!state || !state.endsAt) {
        timedPill.classList.add('hidden');
        timedPill.textContent = '';
        return;
      }
      const tick = () => {
        // The screen may be gone before the clock is: stop with it.
        if (!timedPill.isConnected) {
          clearInterval(api._timedTimer);
          api._timedTimer = null;
          return;
        }
        const left = Math.max(0, state.endsAt - Date.now());
        const secs = Math.ceil(left / 1000);
        timedPill.textContent = `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
        timedPill.classList.toggle('soon', state.warnAt ? Date.now() >= state.warnAt : left <= 30000);
        if (left <= 0) {
          clearInterval(api._timedTimer);
          api._timedTimer = null;
        }
      };
      timedPill.classList.remove('hidden');
      tick();
      api._timedTimer = setInterval(tick, 1000);
    },
    /**
     * An icebreaker card. Built HERE, in the system style, from a message only
     * the server sends -- never from anything a person typed -- for the reason
     * system notices are: a card that looks like Lumi's must be Lumi's. One at
     * a time; a new one replaces the last. `onDismiss` runs when it is waved away.
     */
    showIcebreaker(text, onDismiss) {
      api.hideIcebreaker();
      const dismiss = el('button', {
        class: 'btn btn-ghost btn-xs icebreaker-dismiss',
        type: 'button',
        text: t('Not now'),
        title: t('Dismiss this question'),
        onClick: () => {
          api.hideIcebreaker();
          onDismiss?.();
        },
      });
      // Answering quotes the question above the answer, so "yes, definitely"
      // arriving three messages later still reads as an answer to something.
      const answer = el('button', {
        class: 'btn btn-ghost btn-xs icebreaker-answer',
        type: 'button',
        text: t('Answer this'),
        title: t('Answer this question, quoting it above your reply'),
        onClick: () => startReply({ id: PROMPT_IDS.icebreaker, text: String(text || ''), who: PROMPT_WHO.icebreaker }),
      });
      const node = el('div', { class: 'msg system sys-info icebreaker', role: 'status' }, [
        el('span', { class: 'sys-text' }, [
          el('span', { class: 'icebreaker-lead', text: t('A question, if you want one: ') }),
          el('span', { class: 'icebreaker-text', text: String(text || '') }),
        ]),
        el('div', { class: 'icebreaker-actions' }, [answer, dismiss]),
      ]);
      messages.append(node);
      messages.scrollTop = messages.scrollHeight;
      api._icebreaker = node;
      return node;
    },
    hideIcebreaker() {
      api._icebreaker?.remove();
      api._icebreaker = null;
    },
    /**
     * A chat ended -- the gentle end of a ten-minute one, or the other person
     * moving on, or you skipping. Renders the line in the transcript and, when
     * `onWave` is passed and the server did not say `canWave: false`, the Wave
     * control NEXT TO THE SEARCHING STATUS for WAVE_OFFER_MS, with the copy
     * that says what a wave is and is not. The control is for this pairId and
     * no other: a new match clears it (clearWave).
     */
    showEnded({ pairId, canWave = true, onWave = null, line = 'Time is up. Thanks for the chat.', offerMs = WAVE_OFFER_MS } = {}) {
      api.setTimed(null);
      api.setReveal(null);
      api.hideIcebreaker();
      api.clearWave();
      const node = el('div', { class: 'msg system sys-info ended-line', role: 'status', dataset: { pairId: pairId || '' } }, [
        el('span', { class: 'sys-text', text: line }),
      ]);
      messages.append(node);
      messages.scrollTop = messages.scrollHeight;
      if (!canWave || typeof onWave !== 'function' || !pairId) return node;
      const waveBtn = el('button', {
        class: 'btn btn-secondary btn-sm wave-btn',
        type: 'button',
        text: t('Wave'),
        title: t('Wave at the person you just talked to'),
        onClick: () => {
          waveBtn.disabled = true;
          waveBtn.textContent = 'Waving...';
          onWave(pairId);
        },
      });
      // Not a muted footnote. This is the only moment the whole regulars
      // feature has: miss it and two people who talked for two hours have no
      // way back to each other. It gets a heading, the brand colour, and a
      // dismissal of its own so it never disappears mid-read.
      const dismiss = el('button', {
        class: 'btn btn-ghost btn-sm wave-dismiss',
        type: 'button',
        text: t('No thanks'),
        title: t('Hide this'),
        onClick: () => api.clearWave(pairId),
      });
      const box = el('div', { class: 'wave-box wave-box-prominent', role: 'group', 'aria-label': 'Wave at the person you just talked to', dataset: { pairId } }, [
        el('p', { class: 'wave-title', text: t('Want to talk to them again?') }),
        el('p', { class: 'wave-copy', text: WAVE_COPY }),
        el('div', { class: 'wave-actions' }, [waveBtn, dismiss]),
      ]);
      statusOverlay.append(box);
      api._wave = { pairId, box, timer: setTimeout(() => api.clearWave(pairId), offerMs) };
      return node;
    },
    /**
     * The server's answer to a wave. Replaces the control with one line --
     * "Waved.", or the quiet "They are not remembering anyone today" -- and
     * lets it fade. Ignored for a pair whose control is no longer up.
     */
    waveResult({ pairId, ok, reason } = {}) {
      const w = api._wave;
      if (!w || (pairId && w.pairId !== pairId)) return;
      const text = waveResultText({ ok, reason });
      if (!text) return api.clearWave();
      clearTimeout(w.timer);
      w.box.replaceChildren(el('p', { class: `wave-copy wave-result ${ok ? 'ok' : 'quiet'}`, text }));
      w.timer = setTimeout(() => api.clearWave(), ok ? 4000 : 6000);
      return undefined;
    },
    /** Take the Wave control down (a new match, the timer, or a result shown long enough). */
    clearWave(pairId = null) {
      const w = api._wave;
      if (!w || (pairId && w.pairId !== pairId)) return;
      clearTimeout(w.timer);
      w.box.remove();
      api._wave = null;
    },
    /** Where app.js puts the regulars bell, when the feature exists for this band. */
    bellSlot,
  };

  return { root, api };
}

function addMsg(container, who, contentNode, quote) {
  const node = el('div', { class: `msg ${who}` });
  const q = renderQuote(quote);
  if (q) node.append(q);
  node.append(contentNode);
  container.append(node);
  container.scrollTop = container.scrollHeight;
  return node;
}

/**
 * A still of a received file, as JPEG base64, to attach to a report. Images and
 * videos both reduce to one bounded frame, so a 25 MB clip does not travel to
 * the moderation queue. Audio has nothing to show. Returns null on failure --
 * a report must never depend on the snapshot succeeding.
 */
export function fileEvidence(media, mime) {
  try {
    const isImg = (mime || '').startsWith('image/');
    const isVid = (mime || '').startsWith('video/');
    if (!isImg && !isVid) return null;
    const sw = isImg ? media.naturalWidth : media.videoWidth;
    const sh = isImg ? media.naturalHeight : media.videoHeight;
    if (!sw || !sh) return null;
    const scale = Math.min(1, 1600 / Math.max(sw, sh));
    const c = el('canvas');
    c.width = Math.max(1, Math.round(sw * scale));
    c.height = Math.max(1, Math.round(sh * scale));
    c.getContext('2d').drawImage(media, 0, 0, c.width, c.height);
    return c.toDataURL('image/jpeg', 0.9).split(',')[1] || null;
  } catch {
    return null; // e.g. a tainted canvas -- report without the picture
  }
}

/**
 * The Remove / Report controls that sit under every received file. Removing is
 * immediate and local: whatever was sent leaves the recipient's screen at once,
 * without waiting on a moderator. Reporting attaches THIS file, so the queue
 * sees the actual thing complained about rather than a camera frame.
 */
export function fileControls({ media, mime, wrap, onReport }) {
  const removeBtn = el('button', {
    class: 'btn btn-ghost btn-xs',
    type: 'button',
    text: t('Remove'),
    title: t('Take this off your screen'),
    onClick: () => {
      const src = media.src;
      media.replaceWith(el('div', { class: 'file-removed', text: t('You removed this.') }));
      try {
        if (src && src.startsWith('blob:')) URL.revokeObjectURL(src);
      } catch {
        /* ignore */
      }
      removeBtn.remove();
    },
  });
  const reportBtn = el('button', {
    class: 'btn btn-ghost btn-xs',
    type: 'button',
    text: t('Report'),
    title: t('Report this file to the Lumi team'),
    onClick: () => openReport((reason) => onReport?.(reason, fileEvidence(media, mime))),
  });
  wrap.append(el('div', { class: 'file-actions' }, [removeBtn, reportBtn]));
}

function openReport(cb) {
  // Minimal inline report picker; the admin moderation UI is Phase 3.
  const reasons = [
    [ReportReason.NUDITY, 'Nudity or sexual content'],
    [ReportReason.MINOR_SAFETY, 'A minor is in danger'],
    [ReportReason.HARASSMENT, 'Harassment or bullying'],
    [ReportReason.VIOLENCE, 'Violence'],
    [ReportReason.HATE, 'Hate speech'],
    [ReportReason.SPAM, 'Spam'],
    [ReportReason.OTHER, 'Something else'],
  ];
  const choice = window.prompt(
    'Report this person. Type the number:\n' +
      reasons.map(([, label], i) => `${i + 1}. ${label}`).join('\n'),
  );
  const idx = Number(choice) - 1;
  if (Number.isInteger(idx) && reasons[idx]) {
    cb.onReport?.(reasons[idx][0]);
  }
}

// Moderator kick: a reason is REQUIRED (the citation the admin reviews); a note is
// optional. Unnecessary kicks can cost a moderator their privileges, so the prompt
// is explicit about that.
function openKick(cb) {
  const reasons = [
    [ReportReason.NUDITY, 'Nudity or sexual content'],
    [ReportReason.MINOR_SAFETY, 'A minor is in danger'],
    [ReportReason.HARASSMENT, 'Harassment or bullying'],
    [ReportReason.VIOLENCE, 'Violence or threats'],
    [ReportReason.HATE, 'Hate speech'],
    [ReportReason.SPAM, 'Spam or scams'],
    [ReportReason.OTHER, 'Something else'],
  ];
  const choice = window.prompt(
    'Remove this person from Lumi for 15 minutes.\nYou must cite a reason: type the number:\n' +
      reasons.map(([, label], i) => `${i + 1}. ${label}`).join('\n'),
  );
  if (choice == null) return;
  const idx = Number(choice) - 1;
  if (!Number.isInteger(idx) || !reasons[idx]) return;
  const note = window.prompt('Add a short note for the admin (optional but encouraged):') || '';
  cb.onKick?.(reasons[idx][0], note.slice(0, 500));
}

// ---- inline icons (currentColor) ----
const nextIcon = () =>
  '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 4l8 8-8 8M14 4l6 8-6 8"/></svg>';
const backIcon = () =>
  '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M19 4l-8 8 8 8M10 4l-6 8 6 8"/></svg>';
const closeIcon = () =>
  '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';
const flagIcon = () =>
  '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 21V4h12l-2 4 2 4H5"/></svg>';
const kickIcon = () =>
  '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="7" r="3"/><path d="M3 21v-1a6 6 0 0 1 9-5.2M16 8l5 5M21 8l-5 5"/></svg>';
const sendIcon = () =>
  '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M3 11l18-8-8 18-2-7-8-3z"/></svg>';
